import { useQuery } from "@apollo/client";
import { reviewerHasAccessToAllFacilities } from "encamp-compliance-engine/src/schemas/general/facilityAccess";
import { gql } from "generated-graphql";
import { Permission, UserType } from "generated-graphql/graphql";
import { useMemo } from "react";

export const REVIEWER_FACILITY_ACCESS_QUERY = gql(`
  query ReviewerFacilityAccess($id: ID!) {
    user(id: $id) {
      id
      type
      person { first last }
      facilities { id }
      UserTenant {
        tenantId
        role { permissions }
        tenant {
          id
          partnersTenants { id }
        }
      }
    }
  }
`);

const STAFF_USER_TYPES: UserType[] = [UserType.StaffAdmin];

export function useReviewerFacilityAccessIssue({
  userId,
  name,
  submissionFacilityIds,
  tenantId,
  role = "Reviewer",
}: {
  userId: string | undefined;
  name: string;
  submissionFacilityIds: string[];
  /** Submission's tenant — scopes the all-facility exemption to match the backend rule. */
  tenantId?: string;
  /** Role label used in the warning text. Must stay in sync with renderIssueMessage's regex. */
  role?: "Reviewer" | "Signer";
}): string | null {
  const { data } = useQuery(REVIEWER_FACILITY_ACCESS_QUERY, {
    variables: { id: userId ?? "" },
    skip: !userId,
    // Granting access elsewhere never invalidates this entry, so re-check on mount.
    fetchPolicy: "cache-and-network",
  });

  return useMemo(() => {
    const user = data?.user;
    if (!userId || !user) return null;

    const isStaff = STAFF_USER_TYPES.includes(user.type);

    // Same direct-plus-first-partner pick as the backend's tenantPermissionsWithPartners.
    const userTenants = user.UserTenant ?? [];
    const permissions = tenantId
      ? [
          userTenants.find((ut) => ut.tenantId === tenantId),
          userTenants.find((ut) =>
            (ut.tenant?.partnersTenants ?? []).some((pt) => pt.id === tenantId)
          ),
        ].flatMap((ut) => ut?.role?.permissions ?? [])
      : userTenants.flatMap((ut) => ut.role?.permissions ?? []);

    const hasAllFacilityAccess =
      permissions.includes(Permission.ReadAllFacility) ||
      permissions.includes(Permission.WriteAllFacility);

    const hasAccess = reviewerHasAccessToAllFacilities({
      userFacilityIds: (user.facilities ?? []).map((f) => f.id),
      submissionFacilityIds,
      isStaff,
      hasAllFacilityAccess,
    });

    if (hasAccess) return null;
    return `${role} ${name} must have access to all facilities to access this submission`;
  }, [data, userId, name, submissionFacilityIds, tenantId, role]);
}
